import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { ISLATypesService } from "services/SLATypesService";
import { SETTING_TYPES } from "services/userSettings/SettingTypes";
import { IUserSettingsService } from "services/UserSettingsService";
import { SLATypeModel } from "services/api/cplapi/CPLAPI";
import { RootState } from "setup";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";

const initialState = {
  SLATypes: new Array<SLATypeModel>(),
  LoadingSLATypes: false,
  EditingSLATypes: false,
  SavedColumnOrder: new Array<string>(),
  SlaTargetDate: new Date() || null,
};

export const slaTypesSlice = createSlice({
  name: "slaTypes",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(getSLATypes.pending, (state) => {
        state.LoadingSLATypes = true;
      })
      .addCase(getSLATypes.fulfilled, (state, action) => {
        state.SLATypes = action.payload;
        state.LoadingSLATypes = false;
      })
      .addCase(getSLATypes.rejected, (state) => {
        state.LoadingSLATypes = false;
      })
      .addCase(createSLAType.pending, (state) => {
        state.EditingSLATypes = true;
      })
      .addCase(updateSLAType.pending, (state) => {
        state.EditingSLATypes = true;
      })
      .addCase(deleteSLAType.pending, (state) => {
        state.EditingSLATypes = true;
      })
      .addCase(createSLAType.rejected, (state) => {
        state.EditingSLATypes = false;
      })
      .addCase(updateSLAType.rejected, (state) => {
        state.EditingSLATypes = false;
      })
      .addCase(deleteSLAType.rejected, (state) => {
        state.EditingSLATypes = false;
      })
      .addCase(createSLAType.fulfilled, (state) => {
        toast.success("SLA Type Created");
        state.EditingSLATypes = false;
      })
      .addCase(updateSLAType.fulfilled, (state) => {
        toast.success("SLA Type Updated");
        state.EditingSLATypes = false;
      })
      .addCase(deleteSLAType.fulfilled, (state) => {
        toast.success("SLA Type Deleted");
        state.EditingSLATypes = false;
      })
      .addCase(getSLATypesTableColumnOrder.fulfilled, (state, action) => {
        state.SavedColumnOrder = action.payload;
      })
      .addCase(updateSLATypesTableColumnOrder.fulfilled, (state, action) => {
        state.SavedColumnOrder = action.meta.arg;
        toast.success("Column order saved");
      })
      .addCase(calculateSLATargetDate.fulfilled, (state, action) => {
        state.SlaTargetDate = action.payload;
      });
  },
});

export const getSLATypes = createAsyncThunk("SLATypes/GetSLATypes", async () => {
  const slaTypesService = container.get<ISLATypesService>(SERVICE_TYPES.SLATypesService);
  return await slaTypesService.getSLATypes();
});

export const createSLAType = createAsyncThunk<void, { name: string; targetMinutes: number }, { state: RootState }>(
  "SLATypes/CreateSLATypes",
  async ({ name, targetMinutes }, thunkAPI) => {
    const slaTypesService = container.get<ISLATypesService>(SERVICE_TYPES.SLATypesService);
    await slaTypesService.createSLAType(name, targetMinutes);
    thunkAPI.dispatch(getSLATypes());
  }
);

export const updateSLAType = createAsyncThunk<void, { slaTypeModel: SLATypeModel }, { state: RootState }>(
  "SLATypes/UpdateSLAType",
  async ({ slaTypeModel }, thunkAPI) => {
    const slaTypesService = container.get<ISLATypesService>(SERVICE_TYPES.SLATypesService);
    await slaTypesService.updateSLAType(slaTypeModel);
    thunkAPI.dispatch(getSLATypes());
  }
);

export const deleteSLAType = createAsyncThunk<void, { slaTypeID: number }, { state: RootState }>(
  "SLATypes/DeleteSLAType",
  async ({ slaTypeID }, thunkAPI) => {
    const slaTypesService = container.get<ISLATypesService>(SERVICE_TYPES.SLATypesService);
    await slaTypesService.deleteSLAType(slaTypeID);
    thunkAPI.dispatch(getSLATypes());
  }
);

export const getSLATypesTableColumnOrder = createAsyncThunk("SLATypes/GetSLATypesTableColumnOrder", async () => {
  const userSettingsService = container.get<IUserSettingsService>(SERVICE_TYPES.UserSettingsService);
  return await userSettingsService.getColumnOrder(SETTING_TYPES.SLATypesTableColumnKey);
});

export const updateSLATypesTableColumnOrder = createAsyncThunk(
  "SLATypes/UpdateSLATypesTableColumnOrder",
  async (tableColumnOrder: string[]) => {
    const userSettingsService = container.get<IUserSettingsService>(SERVICE_TYPES.UserSettingsService);
    await userSettingsService.setColumnOrder(tableColumnOrder, SETTING_TYPES.SLATypesTableColumnKey);
  }
);

export const calculateSLATargetDate = createAsyncThunk<Date, { startDate: Date; slaTypeID: number }>(
  "SLATypes/CalculateSLATargetDate",
  async ({ startDate, slaTypeID }) => {
    const slaTypesService = container.get<ISLATypesService>(SERVICE_TYPES.SLATypesService);
    return await slaTypesService.calculateSLATargetDate(startDate, slaTypeID);
  }
);

export const selectSLATypes = (state: RootState) => {
  return state.slaTypes.SLATypes;
};
export const selectLoadingSLATypes = (state: RootState) => {
  return state.slaTypes.LoadingSLATypes;
};
export const selectEditingSLATypes = (state: RootState) => {
  return state.slaTypes.EditingSLATypes;
};

export const selectSavedColumnOrder = (state: RootState) => {
  return state.slaTypes.SavedColumnOrder;
};
export const selectSlaTargetDate = (state: RootState) => {
  return state.slaTypes.SlaTargetDate;
};

export default slaTypesSlice.reducer;
